import { logWarn } from '@/sdkConsole';

// Local-development guidance. The widget resolves the tenant from the page
// hostname, so on a local origin every tenant-scoped config fetch 404s and
// checkout surfaces cannot start. These helpers back the LocalDevStep modal
// and a once-per-page console warning that point the integrating developer at
// the fix: serving from <yourdomain>.localhost, an RFC 6761 loopback alias the
// backend resolves to their site. Client-side UX only — nothing here affects
// requests.

export const LOCAL_DEV_DOCS_URL = 'https://docs.ezoic.com/docs/subscriptions/site-integration/#local-development';
export const TEST_MODE_DOCS_URL = 'https://docs.ezoic.com/docs/subscriptions/testing-sandbox/';

// isLocalHostname reports whether the page runs on a local origin, where the
// hostname can never resolve a tenant. l.ezoic.com is our own local-dev host;
// *.localhost is reserved for loopback by RFC 6761; 0.0.0.0 is a common
// dev-server bind.
export function isLocalHostname(hostname: string): boolean {
  const h = hostname.toLowerCase();
  if (h === 'localhost' || h === 'l.ezoic.com' || h === '0.0.0.0') return true;
  if (h.endsWith('.localhost')) return true;
  if (h === '::1' || h === '[::1]') return true;
  // 127/8 loopback
  return /^127\./.test(h);
}

// isLocalhostAlias reports whether the page already runs on a *.localhost
// alias. The guidance differs there: telling a developer on foo.localhost to
// "serve from <yourdomain>.localhost" reads as nonsense — what they need is
// the full-domain form (mysite.com.localhost, not mysite.localhost).
export function isLocalhostAlias(hostname: string): boolean {
  return hostname.toLowerCase().endsWith('.localhost');
}

// hasDevPort reports whether the page is served on a non-standard port.
// Production sites serve on 80/443 (empty location.port), so an https probe
// failure on a non-standard port is near-certainly a local dev server.
export function hasDevPort(): boolean {
  const port = window.location.port;
  return port !== '' && port !== '80' && port !== '443';
}

let warnedLocalDevTenant = false;

// warnLocalDevTenant fires once per page when a tenant-scoped config fetch
// fails on a local origin: the page host cannot resolve a site, and a silent
// failure reads as a broken widget. notSetup narrows the failure to "the alias
// resolved, but that domain has never begun SM setup" — a different fix than
// the alias advice.
export function warnLocalDevTenant(notSetup = false): void {
  if (warnedLocalDevTenant) return;
  warnedLocalDevTenant = true;
  const host = window.location.hostname;
  const port = window.location.port;
  const portSuffix = port && port !== '80' && port !== '443' ? `:${port}` : '';
  if (notSetup) {
    logWarn(`this page is on ${host}, which resolves to a site that isn't set up for Ezoic Subscriptions — checkout is unavailable until setup is finished in your Ezoic dashboard — see ${LOCAL_DEV_DOCS_URL}`);
    return;
  }
  if (isLocalhostAlias(host)) {
    logWarn(`this page is on ${host}, which matches no site in your Ezoic account — the domain before .localhost must be the full registered domain (mysite.com.localhost, not mysite.localhost) — see ${LOCAL_DEV_DOCS_URL}`);
    return;
  }
  logWarn(`this page is on a local address that matches no site in your Ezoic account. For local development, serve from http://<yourdomain>.localhost${portSuffix} — see ${LOCAL_DEV_DOCS_URL}`);
}
